import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, Edit2, Trash2, Save, X } from 'react-feather';
import {
  puedeGestionarRespuestas,
  tiempoRelativo,
  useActualizarRespuesta,
  useCrearRespuesta,
  useEliminarRespuesta,
  useRespuestas,
} from '../../features/tickets';
import { getItem, STORAGE_KEYS } from '../../services/storage';

const errorBackend = (error, fallback) => error?.response?.data?.error || fallback;

const TicketsRespuestas = ({ headquarter }) => {
  const email = useMemo(() => getItem(STORAGE_KEYS.USER)?.profileObj?.email || '', []);
  const puedeGestionar = puedeGestionarRespuestas(email);

  const { data: respuestas = [], isLoading, isError, refetch } = useRespuestas(headquarter);
  const crear = useCrearRespuesta(headquarter);
  const actualizar = useActualizarRespuesta(headquarter);
  const eliminar = useEliminarRespuesta(headquarter);

  const [titulo, setTitulo] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [formError, setFormError] = useState('');
  const [feedback, setFeedback] = useState(null);

  const [editId, setEditId] = useState(null);
  const [editTitulo, setEditTitulo] = useState('');
  const [editCuerpo, setEditCuerpo] = useState('');
  const [editError, setEditError] = useState('');

  const [confirmId, setConfirmId] = useState(null);

  const onCrear = (e) => {
    e.preventDefault();
    if (!titulo.trim() || !cuerpo.trim()) {
      setFormError('El título y el cuerpo son obligatorios.');
      return;
    }
    setFormError('');
    crear.mutate(
      { titulo: titulo.trim(), cuerpo: cuerpo.trim() },
      {
        onSuccess: () => {
          setTitulo('');
          setCuerpo('');
          setFeedback({ tipo: 'exito', texto: 'Respuesta guardada.' });
        },
        onError: (error) =>
          setFeedback({
            tipo: 'error',
            texto: errorBackend(error, 'No se pudo guardar la respuesta.'),
          }),
      },
    );
  };

  const abrirEdicion = (item) => {
    setConfirmId(null);
    setEditId(item.id);
    setEditTitulo(item.titulo || '');
    setEditCuerpo(item.cuerpo || '');
    setEditError('');
  };

  const cancelarEdicion = () => {
    setEditId(null);
    setEditError('');
  };

  const guardarEdicion = () => {
    if (!editTitulo.trim() || !editCuerpo.trim()) {
      setEditError('El título y el cuerpo son obligatorios.');
      return;
    }
    setEditError('');
    actualizar.mutate(
      { id: editId, titulo: editTitulo.trim(), cuerpo: editCuerpo.trim() },
      {
        onSuccess: () => {
          setEditId(null);
          setFeedback({ tipo: 'exito', texto: 'Respuesta actualizada.' });
        },
        onError: (error) =>
          setEditError(errorBackend(error, 'No se pudo actualizar la respuesta.')),
      },
    );
  };

  const confirmarBorrado = (id) => {
    eliminar.mutate(id, {
      onSuccess: () => {
        setConfirmId(null);
        setFeedback({ tipo: 'exito', texto: 'Respuesta eliminada.' });
      },
      onError: (error) => {
        setConfirmId(null);
        setFeedback({
          tipo: 'error',
          texto: errorBackend(error, 'No se pudo eliminar la respuesta.'),
        });
      },
    });
  };

  return (
    <section className="tickets-panel tickets-respuestas">
      <header className="tickets-listhead">
        <div>
          <h1 className="tickets-listhead__title">Respuestas guardadas</h1>
          <p className="tickets-listhead__sub">
            {puedeGestionar
              ? 'Plantillas reutilizables para insertar en las respuestas a los tickets.'
              : 'Plantillas disponibles para insertar en las respuestas a los tickets.'}
          </p>
        </div>
      </header>

      {feedback && (
        <div
          className={`tickets-respuestas__feedback tickets-respuestas__feedback--${feedback.tipo}`}
          role="status"
        >
          <span>{feedback.texto}</span>
          <button
            type="button"
            className="tickets-tag__remove"
            aria-label="Cerrar aviso"
            onClick={() => setFeedback(null)}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      )}

      {puedeGestionar && (
        <form className="tickets-respuestas__form" onSubmit={onCrear}>
          <div className="tickets-respuestas__field">
            <label className="tickets-props__label" htmlFor="respuesta-titulo">
              Título
            </label>
            <input
              id="respuesta-titulo"
              type="text"
              className="tickets-input"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ej. Solicitud recibida"
              disabled={crear.isPending}
            />
          </div>
          <div className="tickets-respuestas__field">
            <label className="tickets-props__label" htmlFor="respuesta-cuerpo">
              Cuerpo
            </label>
            <textarea
              id="respuesta-cuerpo"
              className="tickets-textarea"
              value={cuerpo}
              onChange={(e) => setCuerpo(e.target.value)}
              placeholder="Escribe el texto de la respuesta…"
              rows={4}
              disabled={crear.isPending}
            />
          </div>
          {formError && <p className="tickets-composer__error">{formError}</p>}
          <div className="tickets-respuestas__form-actions">
            <button
              type="submit"
              className="tickets-btn tickets-btn--primary"
              disabled={crear.isPending}
            >
              <Plus size={16} aria-hidden="true" />
              {crear.isPending ? 'Guardando…' : 'Guardar respuesta'}
            </button>
          </div>
        </form>
      )}

      {isError ? (
        <div className="tickets-empty">
          No se pudieron cargar las respuestas guardadas.
          <div className="tickets-respuestas__retry">
            <button
              type="button"
              className="tickets-btn tickets-btn--secondary"
              onClick={() => refetch()}
            >
              Reintentar
            </button>
          </div>
        </div>
      ) : isLoading ? (
        <div className="tickets-empty">Cargando respuestas…</div>
      ) : respuestas.length === 0 ? (
        <div className="tickets-empty">Aún no hay respuestas guardadas.</div>
      ) : (
        <ul className="tickets-respuestas__list">
          {respuestas.map((item) => {
            const enEdicion = editId === item.id;
            return (
              <li key={item.id} className="tickets-respuestas__item">
                {enEdicion ? (
                  <div className="tickets-respuestas__edit">
                    <div className="tickets-respuestas__field">
                      <label className="tickets-props__label" htmlFor={`edit-titulo-${item.id}`}>
                        Título
                      </label>
                      <input
                        id={`edit-titulo-${item.id}`}
                        type="text"
                        className="tickets-input"
                        value={editTitulo}
                        onChange={(e) => setEditTitulo(e.target.value)}
                        disabled={actualizar.isPending}
                      />
                    </div>
                    <div className="tickets-respuestas__field">
                      <label className="tickets-props__label" htmlFor={`edit-cuerpo-${item.id}`}>
                        Cuerpo
                      </label>
                      <textarea
                        id={`edit-cuerpo-${item.id}`}
                        className="tickets-textarea"
                        value={editCuerpo}
                        onChange={(e) => setEditCuerpo(e.target.value)}
                        rows={4}
                        disabled={actualizar.isPending}
                      />
                    </div>
                    {editError && <p className="tickets-composer__error">{editError}</p>}
                    <div className="tickets-respuestas__item-actions">
                      <button
                        type="button"
                        className="tickets-btn tickets-btn--ghost"
                        onClick={cancelarEdicion}
                        disabled={actualizar.isPending}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="tickets-btn tickets-btn--primary"
                        onClick={guardarEdicion}
                        disabled={actualizar.isPending}
                      >
                        <Save size={15} aria-hidden="true" />
                        {actualizar.isPending ? 'Guardando…' : 'Guardar'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="tickets-respuestas__item-head">
                      <h2 className="tickets-respuestas__item-titulo">{item.titulo}</h2>
                      {puedeGestionar && confirmId !== item.id && (
                        <div className="tickets-respuestas__item-actions">
                          <button
                            type="button"
                            className="tickets-btn tickets-btn--ghost"
                            onClick={() => abrirEdicion(item)}
                          >
                            <Edit2 size={14} aria-hidden="true" />
                            Editar
                          </button>
                          <button
                            type="button"
                            className="tickets-btn tickets-btn--ghost"
                            onClick={() => setConfirmId(item.id)}
                          >
                            <Trash2 size={14} aria-hidden="true" />
                            Borrar
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="tickets-respuestas__item-cuerpo">{item.cuerpo}</p>
                    <p className="tickets-respuestas__item-meta">
                      {item.creadoPor ? `Creada por ${item.creadoPor}` : 'Creada'}
                      {item.fechaAlta ? ` · ${tiempoRelativo(item.fechaAlta)}` : ''}
                    </p>
                    {puedeGestionar && confirmId === item.id && (
                      <div className="tickets-respuestas__confirm">
                        <span>¿Eliminar esta respuesta?</span>
                        <div className="tickets-respuestas__item-actions">
                          <button
                            type="button"
                            className="tickets-btn tickets-btn--ghost"
                            onClick={() => setConfirmId(null)}
                            disabled={eliminar.isPending}
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            className="tickets-btn tickets-btn--danger"
                            onClick={() => confirmarBorrado(item.id)}
                            disabled={eliminar.isPending}
                          >
                            <Trash2 size={14} aria-hidden="true" />
                            {eliminar.isPending ? 'Eliminando…' : 'Eliminar'}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

TicketsRespuestas.propTypes = {
  headquarter: PropTypes.string.isRequired,
};

export default TicketsRespuestas;
